import { HEADER_CELLS } from "@saleor/remittance/constants";
import { ITALICIZED_CELLS } from "@saleor/reports/merchantSettlement/constants";
import { saveAs } from "file-saver";
import xlsx from "xlsx-populate/browser/xlsx-populate";

interface Header {
  [key: string]: string;
}
interface Record {
  [key: string]: any;
}
interface ColumnSizeMap {
  [key: number]: number;
}

type SummaryRecord = string | number;

const FORMAT = {
  CURRENCY: "#,##0.00",
  DATE: "yyyy-mm-dd",
  GENERAL: "General",
  NUMBER: "0.00"
} as const;

const COLOR = {
  BLACK: "000000",
  LEMON: "ffe6a7",
  WHITE: "ffffff"
} as const;

const HEADER_STYLES = {
  bold: true,
  border: true,
  fill: {
    foreground: {
      rgb: COLOR.LEMON
    },
    pattern: "solid",
    type: "pattern"
  },
  horizontalAlignment: "center",
  verticalAlignment: "middle"
};

export async function createReport() {
  const workbook = await xlsx.fromBlankAsync();

  return workbook;
}

export const excelWorksheetBuilder = (
  workbook: xlsx.Workbook,
  sheetName: string
) => {
  const worksheet: xlsx.Worksheet = workbook.addSheet(sheetName);
  worksheet.gridLinesVisible(false);

  const columnSizes: ColumnSizeMap = {};
  let fields = [];

  const addHeaders = (headers: Header) => {
    fields = Object.keys(headers);

    fields.forEach((key, index) => {
      const headerValue = headers[key] ?? "";
      const cell = worksheet.cell(1, index + 1);
      cell.value(headerValue);

      columnSizes[index] = calculateColumnWidth(
        headerValue,
        columnSizes[index]
      );
    });

    worksheet.row(1).style(HEADER_STYLES);
  };

  const addRecords = (records: Record[]) => {
    let rowNumber = 1;

    records.forEach(record => {
      rowNumber++;

      fields.forEach((fieldKey, index) => {
        const value = record[fieldKey] ?? "";
        const cell = worksheet.cell(rowNumber, index + 1);

        cell.value(value);

        if (!isNaN(value)) {
          cell.style("numberFormat", FORMAT.NUMBER);
        }

        columnSizes[index] = calculateColumnWidth(value, columnSizes[index]);
      });

      worksheet.row(rowNumber).style("border", true);
    });

    fields.forEach((_, index) => {
      worksheet.column(index + 1).width(Math.round(columnSizes[index]));
    });
  };

  const addSummaryRecords = (records: SummaryRecord[][]) => {
    records.forEach((record, rowIndex: number) => {
      const rowNumber = rowIndex + 1;

      record.forEach((value, cellIndex: number) => {
        worksheet.cell(rowNumber, cellIndex + 1).value(value);

        columnSizes[cellIndex] = calculateColumnWidth(
          value?.toString(),
          columnSizes[cellIndex]
        );
      });

      worksheet.row(rowNumber).style("fill", COLOR.WHITE);
    });

    Object.values(columnSizes).forEach((width, index) => {
      worksheet.column(index + 1).width(width);
    });

    applySummaryStyles(worksheet);
  };

  return { addHeaders, addRecords, addSummaryRecords };
};

export const downloadReport = async (
  workbook: xlsx.Workbook,
  filename: string
) => {
  removeDefaultSheet(workbook);

  workbook.outputAsync().then(content => {
    saveAs(content, filename);
  });
};

const removeDefaultSheet = (workbook: xlsx.Workbook) => {
  try {
    workbook.deleteSheet("Sheet1");
  } catch (error) {
    console.info(
      "Failed to delete the default sheet. It might be already deleted."
    );
  }
};

const calculateColumnWidth = (content: string, currentWidth: number) => {
  const contentLength = content?.length || 0;
  const minWidth = 10;
  const excelWidth = Math.max(contentLength * 1.15, minWidth);

  return currentWidth ? Math.max(excelWidth, currentWidth) : excelWidth;
};

const applySummaryStyles = (worksheet: xlsx.Worksheet) => {
  HEADER_CELLS.forEach(address => {
    worksheet.cell(address).style("bold", true);
  });

  ITALICIZED_CELLS.forEach(address => {
    worksheet.cell(address).style("italic", true);
  });
};
